import { useCallback, useMemo } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { DateWindow, IsoDate, isValidIsoDate, lengthInDays } from '../math/windows'
import { ORG_DASHBOARD_RANGE_KEYS, OrgDashboardRangeKey, resolveRangeKey } from './org-dashboard-presets'

export type { OrgDashboardRangeKey }

export type OrgDashboardQuery = {
    range: DateWindow
    rangeKey: OrgDashboardRangeKey | 'custom'
    /** Empty means every domain in the organization. */
    domainIds: number[]
}

export type OrgDashboardQueryActions = {
    setRange(key: OrgDashboardRangeKey): void
    setCustomRange(dateWindow: DateWindow): void
    setDomainIds(ids: number[]): void
}

const DEFAULT_RANGE_KEY: OrgDashboardRangeKey = 'last_7_days'
const DOMAIN_ID_PATTERN = /^\d+$/
export const MAX_CUSTOM_RANGE_DAYS = 366

type QueryParameterChanges = Partial<{ range: string; since: string; through: string; domain_ids: string }>

const sortedUniqueValidIds = (ids: number[], validDomainIds: number[]): number[] =>
    [...new Set(ids)].filter((id) => validDomainIds.includes(id)).sort((a, b) => a - b)

/** Selecting every domain is the same as selecting none: both mean "all", so only a strict subset is stored. */
const domainFilterFrom = (ids: number[], validDomainIds: number[]): number[] => {
    const selectedIds = sortedUniqueValidIds(ids, validDomainIds)
    return selectedIds.length === new Set(validDomainIds).size ? [] : selectedIds
}

const parseDomainIds = (rawDomainIds: string | null, validDomainIds: number[]): number[] =>
    rawDomainIds
        ? domainFilterFrom(
              rawDomainIds
                  .split(',')
                  .filter((rawId) => DOMAIN_ID_PATTERN.test(rawId))
                  .map(Number),
              validDomainIds,
          )
        : []

/** Commas in the domain list stay literal so the address reads `domain_ids=1,2`. */
const serializeSearch = (parameters: URLSearchParams): string => {
    const serialized = parameters.toString().replace(/%2C/gi, ',')
    return serialized ? `?${serialized}` : ''
}

/**
 * The organization dashboard's range and domain filter, read from and written to the URL. The URL is the only
 * source of this state, and changes replace the current history entry rather than adding one.
 */
export const useOrgDashboardQuery = (
    validDomainIds: number[],
    latestCompleteDate: IsoDate,
): [OrgDashboardQuery, OrgDashboardQueryActions] => {
    const location = useLocation()
    const history = useHistory()
    const parameters = useMemo(() => new URLSearchParams(location.search), [location.search])
    // Callers typically pass a freshly built array each render; keying on its contents keeps the query and
    // actions identities stable so effects that depend on them do not refire.
    const validDomainIdsKey = validDomainIds.join(',')
    const stableValidDomainIds = useMemo(() => validDomainIds, [validDomainIdsKey])

    const query = useMemo<OrgDashboardQuery>(() => {
        const since = parameters.get('since')
        const through = parameters.get('through')
        const domainIds = parseDomainIds(parameters.get('domain_ids'), stableValidDomainIds)
        if (
            since &&
            through &&
            isValidIsoDate(since) &&
            isValidIsoDate(through) &&
            since <= through &&
            through <= latestCompleteDate &&
            lengthInDays({ since, through }) <= MAX_CUSTOM_RANGE_DAYS
        ) {
            return { range: { since, through }, rangeKey: 'custom', domainIds }
        }
        const requestedRangeKey = parameters.get('range')
        const rangeKey = ORG_DASHBOARD_RANGE_KEYS.find((key) => key === requestedRangeKey) ?? DEFAULT_RANGE_KEY
        return { range: resolveRangeKey(rangeKey, latestCompleteDate), rangeKey, domainIds }
    }, [parameters, stableValidDomainIds, latestCompleteDate])

    const writeQueryParameters = useCallback(
        (changes: QueryParameterChanges, parametersToRemove: string[]) => {
            const nextParameters = new URLSearchParams(location.search)
            parametersToRemove.forEach((name) => nextParameters.delete(name))
            Object.entries(changes).forEach(([name, value]) =>
                value ? nextParameters.set(name, value) : nextParameters.delete(name),
            )
            if (nextParameters.get('range') === DEFAULT_RANGE_KEY) nextParameters.delete('range')
            history.replace({
                pathname: location.pathname,
                search: serializeSearch(nextParameters),
                hash: location.hash,
                state: location.state,
            })
        },
        [history, location.pathname, location.search, location.hash, location.state],
    )

    const actions = useMemo<OrgDashboardQueryActions>(
        () => ({
            setRange: (key) => writeQueryParameters({ range: key }, ['since', 'through']),
            setCustomRange: (dateWindow) => {
                const acceptable =
                    isValidIsoDate(dateWindow.since) &&
                    isValidIsoDate(dateWindow.through) &&
                    dateWindow.since <= dateWindow.through &&
                    dateWindow.through <= latestCompleteDate &&
                    lengthInDays(dateWindow) <= MAX_CUSTOM_RANGE_DAYS
                if (!acceptable) return
                writeQueryParameters({ since: dateWindow.since, through: dateWindow.through }, ['range'])
            },
            setDomainIds: (ids) => {
                const domainFilter = domainFilterFrom(ids, stableValidDomainIds)
                writeQueryParameters({ domain_ids: domainFilter.join(',') }, [])
            },
        }),
        [writeQueryParameters, stableValidDomainIds, latestCompleteDate],
    )

    return [query, actions]
}
